import React, { useState, useEffect } from 'react';
import './BatteryDashboard.css';

const BatteryDashboard = () => {
  const [drivers, setDrivers] = useState([]);
  const [selectedDriver, setSelectedDriver] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    const fetchData = async () => {
      try {
        // Simulating API fetch with the provided data
        const response = await fetch('https://anltcs.noorride.com/driver-battery');
        if (!response.ok) {
          throw new Error('Network response was not ok');
        }
        const data = await response.json();
        const driversArray = Object.entries(data).map(([id, driver]) => ({
          id,
          ...driver
        }));
        
        setDrivers(driversArray);
        
        // Automatically select the first driver when data is loaded
        if (driversArray.length > 0) {
          setSelectedDriver(driversArray[0]);
        }
        
        setLoading(false);
      } catch (err) {
        setError(err.message);
        setLoading(false);
        console.error("Error fetching data:", err);
      }
    };

    fetchData();
  }, []);

  const handleDriverSelect = (driver) => {
    setSelectedDriver(driver);
  };

  const filteredDrivers = drivers.filter(driver =>
    driver.driver_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (driver.login_vehicle && driver.login_vehicle.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const formatLastUpdate = (timestamp) => {
    if (!timestamp || timestamp === "N/A") return "N/A";
    const date = new Date(timestamp);
    return date.toLocaleString();
  };

  if (loading) return <div className="loading">Loading dashboard data...</div>;
  if (error) return <div className="error">Error: {error}</div>;

  return (
    <div className="dashboard">
      <header className="header">
        <h1>EV Fleet Battery Monitoring</h1>
        <div className="search-bar">
          <input
            type="text"
            placeholder="Search drivers or plate numbers..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </header>

      <div className="dashboard-content">
        <div className="driver-list">
          <h2>Drivers ({filteredDrivers.length})</h2>
          <div className="driver-cards">
            {filteredDrivers.map((driver) => (
              <div 
                key={driver.id} 
                className={`driver-card ${selectedDriver?.id === driver.id ? 'selected' : ''}`}
                onClick={() => handleDriverSelect(driver)}
              >
                <div className="driver-status">
                  <div className={`status-indicator ${driver.prediction.current_status.status}`}></div>
                  <div className="driver-name-plate">
                    <span className="name">{driver.driver_name}</span>
                    {driver.login_vehicle && (
                      <span className="plate">{driver.login_vehicle}</span>
                    )}
                  </div>
                </div>
                <div className="driver-info">
                  <span className="vehicle">{driver.vehicle || 'Unknown Vehicle'}</span>
                  <span className="range">{driver.prediction.current_status.remaining_range} km</span>
                </div>
                <div className="last-update">
                  Last update: {formatLastUpdate(driver.last_battery_update)}
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="driver-details">
          {selectedDriver ? (
            <div className="driver-detail-view">
              <div className="driver-header">
                <div>
                  <h2>{selectedDriver.driver_name}</h2>
                  {selectedDriver.login_vehicle && (
                    <span className="plate">Plate: {selectedDriver.login_vehicle}</span>
                  )}
                </div>
                <div className="vehicle-info">
                  <span>{selectedDriver.vehicle || 'Vehicle not specified'}</span>
                  <div className="last-update">
                    Last battery update: {formatLastUpdate(selectedDriver.last_battery_update)}
                  </div>
                </div>
              </div>

              <div className="current-status">
                <div className={`status-card ${selectedDriver.prediction.current_status.status}`}>
                  <div className="status-header">
                    <h3>Current Status</h3>
                    <div className="status-message">
                      <strong>{selectedDriver.prediction.current_status.message}</strong>
                    </div>
                  </div>
                  <div className="status-metrics">
                    <div className="metric">
                      <span>KMs Driven</span>
                      <span>{selectedDriver.prediction.current_status.kms_driven} km</span>
                    </div>
                    <div className="metric">
                      <span>Remaining Range</span>
                      <span>{selectedDriver.prediction.current_status.remaining_range} km</span>
                    </div>
                    <div className="metric">
                      <span>Last Update</span>
                      <span>{selectedDriver.prediction.current_status.last_battery_update}</span>
                    </div>
                  </div>
                </div>

                <div className="warning-points">
                  <h3>Battery Warnings</h3>
                  <div className="warnings">
                    {selectedDriver.prediction.warning_points.turns_yellow_after ? (
                      <div className="warning yellow">
                        <span>Turns yellow after:</span>
                        <span>{selectedDriver.prediction.warning_points.turns_yellow_after}</span>
                      </div>
                    ) : (
                      <div className="warning none">
                        <span>No yellow warning predicted</span>
                      </div>
                    )}
                    {selectedDriver.prediction.warning_points.turns_red_after ? (
                      <div className="warning red">
                        <span>Turns red after:</span>
                        <span>{selectedDriver.prediction.warning_points.turns_red_after}</span>
                      </div>
                    ) : (
                      <div className="warning none">
                        <span>No red warning predicted</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div className="metrics-summary">
                <h3>Performance Metrics</h3>
                <div className="metrics-grid">
                  <div className="metric-card">
                    <span>Avg. Ride Consumption</span>
                    <span>{selectedDriver.prediction.metrics.avg_ride_consumption || 'N/A'} km</span>
                  </div>
                  <div className="metric-card">
                    <span>Estimated Safe Rides</span>
                    <span>{selectedDriver.prediction.metrics.estimated_safe_rides || 'N/A'}</span>
                  </div>
                  <div className="metric-card">
                    <span>Battery Depletes After</span>
                    <span>{selectedDriver.prediction.metrics.battery_depletes_after !== null 
                      ? `${selectedDriver.prediction.metrics.battery_depletes_after} rides` 
                      : 'N/A'}</span>
                  </div>
                  <div className="metric-card">
                    <span>Rides Considered</span>
                    <span>{selectedDriver.prediction.metrics.rides_considered}</span>
                  </div>
                </div>
              </div>

              <div className="ride-timeline">
                <h3>Ride Timeline Predictions</h3>
                {selectedDriver.prediction.prediction_table.length > 0 ? (
                  <div className="timeline">
                    {selectedDriver.prediction.prediction_table.map((ride, index) => (
                      <div key={index} className={`timeline-item ${ride.status}`}>
                        <div className="timeline-marker"></div>
                        <div className="timeline-content">
                          <div className="timeline-header">
                            <span className="ride-number">{ride.ride_number}</span>
                            <span className="ride-type">{ride.type}</span>
                            <span className={`ride-status ${ride.status}`}>{ride.message}</span>
                          </div>
                          <div className="timeline-details">
                            <div className="detail">
                              <span>KMs Consumed:</span>
                              <span>{ride.kms_consumed} km</span>
                            </div>
                            <div className="detail">
                              <span>Remaining Range:</span>
                              <span>{ride.remaining_range} km</span>
                            </div>
                            {ride.ride_info && (
                              <div className="ride-info">
                                <div className="detail">
                                  <span>Rider:</span>
                                  <span>{ride.ride_info.rider_name}</span>
                                </div>
                                <div className="detail">
                                  <span>Distance:</span>
                                  <span>{ride.ride_info.distance} km</span>
                                </div>
                                <div className="detail">
                                  <span>Time:</span>
                                  <span>{ride.ride_info.expected_start} - {ride.ride_info.expected_end}</span>
                                </div>
                                <div className="detail">
                                  <span>From:</span>
                                  <span className="location">{ride.ride_info.start_location}</span>
                                </div>
                                <div className="detail">
                                  <span>To:</span>
                                  <span className="location">{ride.ride_info.end_location}</span>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="no-rides">
                    No ride predictions available
                  </div>
                )}
              </div>
            </div>
          ) : (
            <div className="no-driver-selected">
              <p>Select a driver to view detailed battery information</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default BatteryDashboard;